import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, type Firestore } from 'firebase/firestore';
import type { OneJogConfig } from '../types';

let app: FirebaseApp | null = null;
let db: Firestore | null = null;

/**
 * Inicializa Firebase con la config del cliente o con proyecto OneJog por defecto.
 * Si el cliente no pasa firebaseConfig, se usa variable de entorno / config por sitio en backend.
 */
export function initFirebase(config: OneJogConfig): Firestore | null {
  if (db) return db;

  let firebaseConfig: Record<string, string> | null = null;

  if (config.firebaseConfig) {
    firebaseConfig = config.firebaseConfig as unknown as Record<string, string>;
  } else {
    const defaultConfig = getDefaultFirebaseConfig(config.siteId);
    if (defaultConfig.apiKey && defaultConfig.projectId) {
      firebaseConfig = defaultConfig;
    }
  }

  if (!firebaseConfig?.apiKey) {
    if (typeof console !== 'undefined') {
      console.warn('[Kenbun] Firebase no configurado. Añade data-firebase-* al script o configura VITE_FIREBASE_* en build.');
    }
    return null;
  }

  app = initializeApp(firebaseConfig, `kenbun-${config.siteId}`);
  db = getFirestore(app);
  return db;
}

/** Config por defecto: Vite reemplaza import.meta.env.VITE_* al hacer build (desde .env) */
function getDefaultFirebaseConfig(_siteId: string): Record<string, string> {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY ?? '';
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'kenbun-default';
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? `${projectId}.firebaseapp.com`;
  const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? `${projectId}.appspot.com`;
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '';
  const appId = import.meta.env.VITE_FIREBASE_APP_ID ?? '';

  return {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
  };
}

export function getDb(): Firestore | null {
  return db;
}
